import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Users, Download, Search, ChevronLeft, ChevronRight,
  Calendar, Building2, Phone, Mail, MessageSquare, CheckCircle,
  Clock, XCircle, Loader2, X, Save, ExternalLink, Pencil, Trash2,
  Send, Eye, ArrowRight, Zap, CalendarPlus, AlertTriangle,
  Video, Copy, Ban, RotateCcw, ShieldAlert, PhoneCall,
  StickyNote, Link2, Inbox, MailCheck, MailX, Move,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  getLeads, getLeadById, getLeadStats, updateLeadStatus, updateLeadNotes,
  editLead, deleteLead, sendLeadEmail, onboardLead, exportLeads,
  sendLeadStageEmail, previewLeadStageEmail, updateLeadDemo, markLeadSpam, logLeadActivity,
} from '@/services/apiClient';
import { PageHeader } from '@/components/ux';
import CreateCompanyWizard, { CredentialsBanner } from '@/components/superadmin/CreateCompanyWizard';
import { cn } from '@/lib/utils';

// ── Constants ──────────────────────────────────────────────────────────────────

const PIPELINE = [
  { key: 'new',            label: 'New',            color: 'bg-blue-500',    text: 'text-blue-600' },
  { key: 'contacted',      label: 'Contacted',      color: 'bg-amber-500',   text: 'text-amber-600' },
  { key: 'demo_scheduled', label: 'Demo Scheduled', color: 'bg-violet-500',  text: 'text-violet-600' },
  { key: 'demo_completed', label: 'Demo Done',      color: 'bg-indigo-500',  text: 'text-indigo-600' },
  { key: 'onboarded',      label: 'Onboarded',      color: 'bg-emerald-500', text: 'text-emerald-600' },
  { key: 'lost',           label: 'Lost',           color: 'bg-red-500',     text: 'text-red-600' },
];

// Stages that have a canonical email behind them.
//
// 'new' and 'lost' are internal — the lead is never told about either.
// 'demo_scheduled' deliberately has none: the demo invitation is sent by
// Google Calendar, which owns the Meet link and the reminders, so a portal
// confirmation on top would be a duplicate.
const STAGE_EMAILS = {
  contacted:      'Contacted',
  demo_completed: 'Demo follow-up',
  onboarded:      'Welcome',
};

const DEMO_DURATIONS = [30, 45, 60];

// The wizard's size select only accepts these exact values, while leads store
// free text from the website form ("1000+ employees"). Anything that doesn't
// map cleanly is left blank rather than guessed at.
const WIZARD_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];
function toWizardSize(raw) {
  const s = String(raw || '').replace(/employees/i, '').trim();
  return WIZARD_SIZES.includes(s) ? s : '';
}

// Seeds the company wizard from what the lead already told us, so onboarding
// is not a retyping exercise.
function leadToCompanySeed(lead) {
  const parts = String(lead.name || '').trim().split(/\s+/);
  return {
    companyName:    lead.company || '',
    contactEmail:   lead.email || '',
    companySize:    toWizardSize(lead.companySize),
    adminFirstName: parts[0] || '',
    adminLastName:  parts.slice(1).join(' '),
  };
}

/**
 * Builds a pre-filled Google Calendar event.
 *
 * Google mints the Meet link and emails the invitation to the guest itself, so
 * there's no OAuth to set up here and no second confirmation from the portal.
 */
function buildGoogleCalendarUrl(lead, startsAt, durationMins) {
  const start = new Date(startsAt);
  const end   = new Date(start.getTime() + durationMins * 60000);
  const stamp = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

  const details = [
    `EchoHire product demo for ${lead.name} (${lead.company}).`,
    '',
    `Contact: ${lead.email}${lead.phone ? ` · ${lead.phone}` : ''}`,
    lead.companySize ? `Company size: ${lead.companySize}` : '',
    `Interest: ${lead.intent === 'book_demo' ? 'Book a demo' : 'Free trial'}`,
    lead.message ? `\nWhat they asked for:\n${lead.message}` : '',
    '',
    'Add Google Meet before saving so the guest receives the joining link.',
  ].filter(Boolean).join('\n');

  // Built by hand rather than with URLSearchParams: that percent-encodes the
  // range separator as %2F, which Google does not parse — it then silently
  // falls back to a slot near the current time instead of the one chosen.
  // `stamp` only ever yields [0-9TZ], so the dates value needs no escaping.
  const query = [
    'action=TEMPLATE',
    `text=${encodeURIComponent(`EchoHire Demo — ${lead.company}`)}`,
    `dates=${stamp(start)}/${stamp(end)}`,
    `details=${encodeURIComponent(details)}`,
    `add=${encodeURIComponent(lead.email)}`,
  ].join('&');
  return `https://calendar.google.com/calendar/render?${query}`;
}

const BOOK_DEMO_URL = 'https://echohire.in/book-demo';

// ── Helpers ────────────────────────────────────────────────────────────────────

function statusMeta(s) {
  return PIPELINE.find(p => p.key === s) || { label: s, color: 'bg-muted', text: 'text-muted-foreground' };
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
}

// A `datetime-local` input needs local wall-clock time; toISOString would shift
// the value by the timezone offset and show the wrong hour.
function toLocalInput(d) {
  if (!d) return '';
  const dt = new Date(d);
  return new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

/**
 * The inverse: a `datetime-local` value carries no timezone, and the server
 * runs in UTC, so sending it raw makes Node read the wall-clock time as UTC —
 * storing a demo 5h30m late for an IST user. Send an absolute instant instead.
 */
function toIsoInstant(localValue) {
  if (!localValue) return null;
  const d = new Date(localValue);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Opt-in email toggle shared by every stage-changing dialog.
 *
 * Always defaults to off: moving a lead through the pipeline is bookkeeping,
 * and it should never mail a customer unless that was explicitly asked for.
 */
function SendEmailToggle({ stage, checked, onChange, disabled }) {
  const label = STAGE_EMAILS[stage];
  if (!label) {
    return (
      <p className="rounded-md bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
        {stage === 'demo_scheduled'
          ? 'The demo invitation is sent by Google Calendar, so the portal sends nothing for this stage.'
          : "No email is sent for this stage — it's an internal status only."}
      </p>
    );
  }
  return (
    <label className={cn('flex cursor-pointer items-start gap-2.5 rounded-md border border-border bg-muted/30 px-3 py-2.5',
      disabled && 'cursor-not-allowed opacity-60')}>
      <input
        type="checkbox"
        className="mt-0.5 accent-primary"
        checked={checked}
        disabled={disabled}
        onChange={e => onChange(e.target.checked)}
      />
      <span className="text-xs leading-relaxed">
        <span className="font-medium text-foreground">Also send the “{label}” email</span>
        <span className="block text-muted-foreground">
          {checked ? 'The lead will be emailed now.' : 'Off — the lead will not be contacted.'}
        </span>
      </span>
    </label>
  );
}

// Intent + status → pre-written email
function buildDefaultEmail(lead) {
  const first   = (lead.name || '').split(' ')[0] || 'there';
  const company = lead.company || 'your company';

  if (lead.intent === 'book_demo') {
    if (lead.status === 'new') {
      return {
        subject: `Your EchoHire demo request — let's pick a time`,
        body: `Hi ${first},

Thank you for reaching out to EchoHire! We'd love to show you how AI-powered interviews can transform hiring at ${company}.

Book a 20-minute demo at a time that suits you:
👉 ${BOOK_DEMO_URL}

Or simply reply with your availability and we'll get it set up.

What we'll cover:
• AI video interviews that run automatically — no manual effort
• Real-time proctoring to ensure hiring integrity
• Instant scoring reports so you focus only on the best candidates
• How teams like yours cut time-to-hire by up to 60%

Looking forward to connecting!

Best,
Devansh Kumar
EchoHire — AI Interview Platform
https://echohire.in`,
      };
    }
    if (lead.status === 'contacted') {
      return {
        subject: `Following up — EchoHire demo for ${company}`,
        body: `Hi ${first},

Just following up on your EchoHire demo request — we're ready whenever you are!

A quick reminder of what you'll see:
• How companies run hundreds of AI interviews simultaneously
• Live scoring and candidate ranking — no manual review needed
• Full setup in under 10 minutes

Pick a slot here: ${BOOK_DEMO_URL}

Happy to work around your schedule — just reply with what works.

Best,
Devansh Kumar
EchoHire
https://echohire.in`,
      };
    }
    if (lead.status === 'demo_scheduled') {
      return {
        subject: `Reminder: Your EchoHire demo on ${lead.demoScheduledAt ? fmtDate(lead.demoScheduledAt) : 'the scheduled date'}`,
        body: `Hi ${first},

Just a friendly reminder about your EchoHire demo scheduled for ${lead.demoScheduledAt ? fmtDateTime(lead.demoScheduledAt) : 'our upcoming call'}.

If you need to reschedule, no problem — just reply and we'll find another time.

See you soon!

Best,
Devansh Kumar
EchoHire
https://echohire.in`,
      };
    }
    if (lead.status === 'demo_completed') {
      return {
        subject: `Next steps after your EchoHire demo`,
        body: `Hi ${first},

Great connecting with you! Thank you for taking the time to see EchoHire in action.

To recap what we covered:
• AI-powered video interviews running automatically, 24/7
• Instant scoring, reports, and candidate ranking
• Full proctoring to maintain hiring integrity

Ready to move forward for ${company}?

Option 1 — Start a free 14-day trial (no credit card needed)
Option 2 — Custom plan tailored to your hiring volume

Just reply and I'll get everything set up within 24 hours.

Best,
Devansh Kumar
EchoHire
https://echohire.in`,
      };
    }
  }

  if (lead.intent === 'free_trial') {
    if (lead.status === 'new') {
      return {
        subject: `Setting up your EchoHire free trial`,
        body: `Hi ${first},

Thank you for your interest in EchoHire! We're excited to get ${company} started with AI-powered interviews.

Your 14-day free trial includes:
• Unlimited AI interview sessions
• Real-time proctoring & anti-cheat
• Instant scoring and detailed candidate reports
• Up to 3 admin users
• Priority email support

To activate your trial, could you share:
1. How many open roles are you currently hiring for?
2. What type of roles? (Technical / Non-technical / Both)

This helps us pre-configure the platform for you. We'll have your account ready within a few hours.

Best,
Devansh Kumar
EchoHire — AI Interview Platform
https://echohire.in`,
      };
    }
    if (lead.status === 'contacted') {
      return {
        subject: `Still interested in EchoHire? Your trial is waiting`,
        body: `Hi ${first},

Just checking in — your EchoHire free trial for ${company} is ready to activate!

With your trial you can immediately:
• Post your first AI interview and invite candidates
• See live transcripts, scores, and proctoring reports
• Compare candidates side-by-side in minutes

It takes less than 10 minutes to get started. Just reply and I'll send your login credentials.

Best,
Devansh Kumar
EchoHire
https://echohire.in`,
      };
    }
    if (lead.status === 'demo_completed') {
      return {
        subject: `Your EchoHire trial account is ready`,
        body: `Hi ${first},

Following our conversation, I've prepared a trial account for ${company}.

Your 14-day trial includes:
• Up to 50 interview sessions
• Full AI scoring & reports
• 3 admin user seats
• Email + chat support

Reply here and I'll send your login credentials right away so you can start today.

Best,
Devansh Kumar
EchoHire
https://echohire.in`,
      };
    }
  }

  return {
    subject: `Following up — EchoHire for ${company}`,
    body: `Hi ${first},

I wanted to follow up on your interest in EchoHire. Is there anything we can help clarify about how EchoHire can help ${company} streamline hiring?

Happy to jump on a quick call or answer any questions by email.

Best,
Devansh Kumar
EchoHire — AI Interview Platform
https://echohire.in`,
  };
}

// ── Stat Cards ─────────────────────────────────────────────────────────────────

function StatCards({ stats, intentStats, loading }) {
  const cards = [
    { label: 'Total',        value: stats.total,           color: 'text-foreground',    bg: 'bg-muted/40' },
    { label: 'New',          value: stats.new,             color: 'text-blue-600',      bg: 'bg-blue-50 dark:bg-blue-950/30' },
    { label: 'Demo Booked',  value: stats.demo_scheduled,  color: 'text-violet-600',    bg: 'bg-violet-50 dark:bg-violet-950/30' },
    { label: 'Onboarded',    value: stats.onboarded,       color: 'text-emerald-600',   bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
    { label: 'Lost',         value: stats.lost,            color: 'text-red-600',       bg: 'bg-red-50 dark:bg-red-950/30' },
    { label: 'Demo Intent',  value: intentStats?.book_demo,  color: 'text-sky-600',     bg: 'bg-sky-50 dark:bg-sky-950/30' },
    { label: 'Trial Intent', value: intentStats?.free_trial, color: 'text-teal-600',    bg: 'bg-teal-50 dark:bg-teal-950/30' },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
      {cards.map(({ label, value, color, bg }) => (
        <div key={label} className={cn('rounded-lg border border-border p-3', bg)}>
          <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
          <p className={cn('mt-0.5 text-xl font-bold', color)}>{loading ? '—' : (value ?? 0)}</p>
        </div>
      ))}
    </div>
  );
}

// ── Send Email Modal ────────────────────────────────────────────────────────────

function SendEmailModal({ lead, onClose, onSent }) {
  const def = buildDefaultEmail(lead);
  const [subject, setSubject] = useState(def.subject);
  const [body, setBody]       = useState(def.body);
  const [step, setStep]       = useState('compose'); // 'compose' | 'preview'
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Subject and body are required.'); return; }
    setSending(true);
    try {
      await sendLeadEmail(lead._id, { subject: subject.trim(), body: body.trim() });
      toast.success(`Email sent to ${lead.email}`);
      onSent?.();
      onClose();
    } catch (e) { toast.error(e.message || 'Failed to send.'); }
    finally { setSending(false); }
  };

  const intentCls = lead.intent === 'book_demo'
    ? 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/20'
    : 'bg-teal-500/10 text-teal-700 dark:text-teal-400 border-teal-500/20';
  const intentLabel = lead.intent === 'book_demo' ? 'Demo Intent' : 'Trial Intent';

  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-4 w-4 text-primary" /> Send Email — {lead.name}
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap pt-0.5">
            <span>To: <strong>{lead.email}</strong></span>
            <span className="text-muted-foreground">·</span>
            <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold', intentCls)}>
              {intentLabel}
            </span>
            <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
              {statusMeta(lead.status).label}
            </span>
          </DialogDescription>
        </DialogHeader>

        {step === 'compose' && (
          <div className="space-y-3 pt-1">
            <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5 text-xs text-primary">
              <Zap className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Pre-written based on intent (<strong>{intentLabel}</strong>) and current stage (<strong>{statusMeta(lead.status).label}</strong>). Edit freely before sending.</span>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Subject</Label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} className="text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Body</Label>
              <Textarea value={body} onChange={e => setBody(e.target.value)} rows={14} className="font-sans text-sm resize-y" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button variant="outline" onClick={() => setStep('preview')} disabled={!subject.trim() || !body.trim()} className="gap-1.5">
                <Eye className="h-3.5 w-3.5" /> Preview & Verify
              </Button>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4 pt-1">
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="bg-muted/40 border-b border-border px-4 py-2.5 space-y-1 text-xs">
                <div className="flex gap-2"><span className="font-semibold text-muted-foreground w-10 shrink-0">From:</span><span>EchoHire &lt;contact@echohire.in&gt;</span></div>
                <div className="flex gap-2"><span className="font-semibold text-muted-foreground w-10 shrink-0">To:</span><span>{lead.name} &lt;{lead.email}&gt;</span></div>
                <div className="flex gap-2"><span className="font-semibold text-muted-foreground w-10 shrink-0">Sub:</span><span className="font-medium text-foreground">{subject}</span></div>
              </div>
              <div className="px-4 py-4 text-sm leading-relaxed whitespace-pre-line text-foreground max-h-72 overflow-y-auto">
                {body}
              </div>
            </div>
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>This email will be sent immediately to <strong>{lead.email}</strong>. Review carefully before confirming.</span>
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <Button variant="outline" onClick={() => setStep('compose')} className="gap-1.5">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSend} disabled={sending} className="gap-2">
                  {sending ? <><Loader2 className="h-4 w-4 animate-spin" />Sending…</> : <><Send className="h-4 w-4" />Confirm & Send</>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Lead Modal ─────────────────────────────────────────────────────────────

function EditLeadModal({ lead, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: lead.name || '', phone: lead.phone || '',
    company: lead.company || '', companySize: lead.companySize || '',
    message: lead.message || '', notes: lead.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.company.trim()) { toast.error('Name and company are required.'); return; }
    setSaving(true);
    try { await editLead(lead._id, form); toast.success('Lead updated.'); onSaved(); }
    catch (e) { toast.error(e.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="h-4 w-4 text-primary" /> Edit Lead</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Name <span className="text-red-500">*</span></Label><Input value={form.name} onChange={e => set('name', e.target.value)} className="text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => set('phone', e.target.value)} className="text-sm" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Company <span className="text-red-500">*</span></Label><Input value={form.company} onChange={e => set('company', e.target.value)} className="text-sm" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Company Size</Label><Input value={form.companySize} onChange={e => set('companySize', e.target.value)} placeholder="e.g. 50-100" className="text-sm" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Message from lead</Label><Textarea value={form.message} onChange={e => set('message', e.target.value)} rows={3} className="text-sm resize-none" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Internal Notes</Label><Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} placeholder="Private notes…" className="text-sm resize-none" /></div>
        </div>
        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete Dialog ───────────────────────────────────────────────────────────────

function DeleteLeadDialog({ lead, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const handle = async () => {
    setDeleting(true);
    try { await deleteLead(lead._id); toast.success('Lead deleted.'); onDeleted(); }
    catch (e) { toast.error(e.message || 'Failed.'); }
    finally { setDeleting(false); }
  };
  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive"><Trash2 className="h-4 w-4" /> Delete Lead</DialogTitle>
          <DialogDescription>Permanently delete <strong>{lead.name}</strong> from <strong>{lead.company}</strong>? This cannot be undone.</DialogDescription>
        </DialogHeader>
        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancel</Button>
          <Button variant="destructive" onClick={handle} disabled={deleting} className="gap-2">
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Schedule Demo Modal ─────────────────────────────────────────────────────────

function ScheduleDemoModal({ lead, onClose, onSaved }) {
  const [dateTime, setDateTime] = useState(toLocalInput(lead?.demoScheduledAt));
  const [duration, setDuration] = useState(45);
  const [meetLink, setMeetLink] = useState(lead?.demoMeetLink || '');
  const [opened, setOpened]     = useState(false);
  const [saving, setSaving]     = useState(false);

  const linkLooksWrong = meetLink.trim() && !/^https:\/\//i.test(meetLink.trim());

  const openCalendar = () => {
    if (!dateTime) { toast.error('Pick the date and time first.'); return; }
    window.open(buildGoogleCalendarUrl(lead, dateTime, duration), '_blank', 'noopener');
    setOpened(true);
  };

  const handle = async () => {
    if (!dateTime) { toast.error('Please select a date and time.'); return; }
    if (linkLooksWrong) { toast.error('The meeting link must start with https://'); return; }
    setSaving(true);
    try {
      // No email from the portal — Google Calendar sends the invitation.
      await updateLeadStatus(lead._id, {
        status: 'demo_scheduled',
        demoScheduledAt: toIsoInstant(dateTime),
        demoMeetLink: meetLink.trim(),
        sendEmail: false,
      });
      toast.success('Demo saved.');
      onSaved();
    }
    catch (e) { toast.error(e.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarPlus className="h-4 w-4 text-violet-500" /> Schedule Demo</DialogTitle>
          <DialogDescription>For <strong>{lead?.name}</strong> from <strong>{lead?.company}</strong>.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Date &amp; Time</Label>
              <Input type="datetime-local" value={dateTime} onChange={e => setDateTime(e.target.value)} min={toLocalInput(new Date())} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Duration</Label>
              <select
                value={duration}
                onChange={e => setDuration(Number(e.target.value))}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                {DEMO_DURATIONS.map(m => <option key={m} value={m}>{m} min</option>)}
              </select>
            </div>
          </div>

          {/* Google Calendar owns the invitation, the Meet link and the reminders */}
          <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2.5">
            <Button type="button" onClick={openCalendar} disabled={!dateTime} className="w-full gap-2">
              <Calendar className="h-4 w-4" /> Schedule in Google Calendar
            </Button>
            <ol className="ml-4 list-decimal space-y-0.5 text-[11px] leading-relaxed text-muted-foreground">
              <li>Opens a new event pre-filled with the date, the agenda and {lead?.name} as a guest.</li>
              <li>Click <strong>Add Google Meet video conferencing</strong>.</li>
              <li>Save, and choose <strong>Send</strong> when Google offers to email the guest.</li>
            </ol>
            {opened && (
              <p className="flex items-start gap-1.5 rounded bg-emerald-500/10 px-2 py-1.5 text-[11px] text-emerald-700 dark:text-emerald-400">
                <CheckCircle className="mt-px h-3 w-3 shrink-0" />
                Calendar opened. Google sends the invitation — the portal will not send a confirmation email.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              <Video className="h-3.5 w-3.5 text-muted-foreground" /> Meet link <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              placeholder="https://meet.google.com/abc-defg-hij"
              value={meetLink}
              onChange={e => setMeetLink(e.target.value)}
              className={cn(linkLooksWrong && 'border-destructive focus-visible:ring-destructive')}
            />
            <p className="text-[11px] text-muted-foreground">
              {linkLooksWrong
                ? <span className="text-destructive">The link must start with https://</span>
                : 'Paste the link Google created so it shows on the lead — for your reference only, the guest already has it from the invite.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handle} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />}
            Save Demo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Move Stage Modal ────────────────────────────────────────────────────────────

/**
 * Manual pipeline control: set any stage, in any direction.
 *
 * Backward moves are shown separately so they read as deliberate rather than a
 * mis-click, and the email stays opt-in for all of them.
 */
function MoveStageModal({ lead, presetTarget, onClose, onSaved }) {
  const currentIdx = PIPELINE.findIndex(p => p.key === lead.status);
  const [target, setTarget]       = useState(presetTarget || '');
  const [note, setNote]           = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [saving, setSaving]       = useState(false);

  // Changing target resets the toggle, so a tick meant for one stage can't
  // silently carry over and mail the wrong template.
  const pick = (key) => { setTarget(key); setSendEmail(false); };

  const handle = async () => {
    if (!target) { toast.error('Pick a stage to move to.'); return; }
    setSaving(true);
    try {
      const res = await updateLeadStatus(lead._id, { status: target, sendEmail, note });
      if (sendEmail && res?.emailError) toast.warning(`Stage changed, but the email failed: ${res.emailError}`);
      else if (sendEmail)               toast.success(`Moved to “${statusMeta(target).label}” and emailed.`);
      else                              toast.success(`Moved to “${statusMeta(target).label}”. No email sent.`);
      onSaved(target);
    }
    catch (e) { toast.error(e.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const renderChip = (p) => {
    const idx = PIPELINE.findIndex(x => x.key === p.key);
    const isCurrent = p.key === lead.status;
    return (
      <button
        key={p.key}
        type="button"
        disabled={isCurrent}
        onClick={() => pick(p.key)}
        className={cn(
          'flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium transition-all text-left',
          isCurrent      ? 'cursor-not-allowed border-border bg-muted text-muted-foreground'
          : target === p.key ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary'
                           : 'border-border hover:border-primary/50 hover:bg-muted/50',
        )}
      >
        <span className={cn('h-2 w-2 rounded-full shrink-0', p.color)} />
        <span className="flex-1">{p.label}</span>
        {isCurrent && <span className="text-[10px] uppercase tracking-wide">current</span>}
        {!isCurrent && idx < currentIdx && <span className="text-[10px] text-muted-foreground">back</span>}
      </button>
    );
  };

  const forward  = PIPELINE.filter(p => PIPELINE.findIndex(x => x.key === p.key) > currentIdx);
  const backward = PIPELINE.filter(p => PIPELINE.findIndex(x => x.key === p.key) < currentIdx);

  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Move className="h-4 w-4 text-primary" /> Move Pipeline Stage</DialogTitle>
          <DialogDescription>
            Currently <strong>{statusMeta(lead.status).label}</strong>. Pick any stage — forward or back.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1 max-h-[55vh] overflow-y-auto">
          {forward.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Move forward</p>
              <div className="grid gap-1.5">{forward.map(renderChip)}</div>
            </div>
          )}
          {backward.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Move back</p>
              <div className="grid gap-1.5">{backward.map(renderChip)}</div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Note (optional)</Label>
            <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Why is it moving?" className="text-sm" />
          </div>

          {target && <SendEmailToggle stage={target} checked={sendEmail} onChange={setSendEmail} disabled={saving} />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handle} disabled={saving || !target} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {sendEmail ? 'Move & Send Email' : 'Move Quietly'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Spam Modal ──────────────────────────────────────────────────────────────────

function SpamModal({ lead, onClose, onSaved }) {
  const marking = !lead.isSpam;
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    try {
      await markLeadSpam(lead._id, { isSpam: marking, reason });
      toast.success(marking ? 'Moved to Spam.' : 'Restored from Spam.');
      onSaved(marking);
    }
    catch (e) { toast.error(e.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {marking ? <><ShieldAlert className="h-4 w-4 text-amber-500" /> Mark as Fake / Spam</>
                     : <><RotateCcw className="h-4 w-4 text-emerald-500" /> Restore Lead</>}
          </DialogTitle>
          <DialogDescription>
            {marking
              ? <>Moves <strong>{lead.name}</strong> to the Spam tab and removes it from the pipeline and all counts. Its stage is kept, so restoring puts it back exactly where it was.</>
              : <>Returns <strong>{lead.name}</strong> to the pipeline at its previous stage, <strong>{statusMeta(lead.status).label}</strong>.</>}
          </DialogDescription>
        </DialogHeader>
        {marking && (
          <div className="space-y-1.5 py-1">
            <Label className="text-xs">Reason (optional)</Label>
            <Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Bot submission, fake domain…" className="text-sm" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant={marking ? 'default' : 'outline'} onClick={handle} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : marking ? <Ban className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
            {marking ? 'Move to Spam' : 'Restore'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Log Activity Modal ──────────────────────────────────────────────────────────

function LogActivityModal({ lead, onClose, onSaved }) {
  const [type, setType] = useState('call');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    if (!note.trim()) { toast.error('Add a note.'); return; }
    setSaving(true);
    try { await logLeadActivity(lead._id, { type, note: note.trim() }); toast.success('Logged.'); onSaved(); }
    catch (e) { toast.error(e.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><StickyNote className="h-4 w-4 text-primary" /> Log Activity</DialogTitle>
          <DialogDescription>Record a call or note so it shows on the timeline alongside the emails.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="flex gap-2">
            {[{ k: 'call', label: 'Call', Icon: PhoneCall }, { k: 'note', label: 'Note', Icon: StickyNote }].map(({ k, label, Icon }) => (
              <button
                key={k} type="button" onClick={() => setType(k)}
                className={cn('flex flex-1 items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-all',
                  type === k ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted/50')}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>
          <Textarea rows={4} value={note} onChange={e => setNote(e.target.value)} placeholder="What happened?" className="text-sm" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handle} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Log
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Mark Lost Modal ─────────────────────────────────────────────────────────────

function MarkLostModal({ lead, onClose, onSaved }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const handle = async () => {
    setSaving(true);
    try { await updateLeadStatus(lead._id, { status: 'lost', lostReason: reason }); toast.success('Lead marked as lost.'); onSaved(); }
    catch (e) { toast.error(e.message || 'Failed.'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="text-destructive">Mark as Lost</DialogTitle></DialogHeader>
        <div className="space-y-2 py-1">
          <Label className="text-xs">Reason (optional)</Label>
          <Textarea placeholder="e.g. No response after 3 follow-ups…" value={reason} onChange={e => setReason(e.target.value)} rows={3} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="destructive" onClick={handle} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
            Mark as Lost
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Onboard Lead ────────────────────────────────────────────────────────────────

/**
 * Onboards a lead through the same wizard as the Companies page, seeded from
 * the lead, then links the new company back to it.
 *
 * Replaces the old two-window dance where you created the company by hand on
 * another page and pasted its ID back in.
 */
function OnboardLeadWizard({ lead, onClose, onSaved }) {
  const handleCreated = async (result) => {
    try {
      // Silent like every other stage move — the "Welcome" stage-email button
      // sends the welcome when it is actually wanted.
      await onboardLead(lead._id, { companyId: result.companyId, sendEmail: false });
      toast.success(`${lead.company} onboarded and linked to this lead.`);
      onSaved(result);
    } catch (e) {
      // The company exists at this point; only the lead link failed, so say so
      // rather than implying nothing happened.
      toast.error(`Company created, but linking the lead failed: ${e.message || 'unknown error'}`);
      onSaved(result);
    }
  };

  return (
    <CreateCompanyWizard
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      initialValues={leadToCompanySeed(lead)}
      onCreated={handleCreated}
    />
  );
}

// ── Stage Email Confirmation ────────────────────────────────────────────────────

/**
 * Shows the exact email before it is sent.
 *
 * The body is rendered in a sandboxed iframe: it is our own template, but email
 * HTML carries its own styling that would otherwise leak into the page.
 */
function StageEmailConfirmModal({ lead, stage, onClose, onSent }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    previewLeadStageEmail(lead._id, stage)
      .then(res => { if (alive) setPreview(res); })
      .catch(e => { if (alive) setError(e.message || 'Could not render this email.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [lead._id, stage]);

  const handleSend = async () => {
    setSending(true);
    try {
      await sendLeadStageEmail(lead._id, stage);
      toast.success(`“${STAGE_EMAILS[stage]}” email sent to ${lead.email}`);
      onSent();
    } catch (e) {
      toast.error(e.message || 'Could not send the email.');
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={v => !v && !sending && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MailCheck className="h-4 w-4 text-primary" /> Send “{STAGE_EMAILS[stage]}” email?
          </DialogTitle>
          <DialogDescription>This is exactly what {lead.name} will receive.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="space-y-1 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
            <p><span className="text-muted-foreground">To:</span> <strong className="text-foreground">{lead.email}</strong></p>
            <p className="break-words">
              <span className="text-muted-foreground">Subject:</span>{' '}
              <strong className="text-foreground">{loading ? '…' : preview?.subject || '—'}</strong>
            </p>
          </div>

          {loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Rendering the email…
            </div>
          )}
          {error && (
            <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" /> {error}
            </p>
          )}
          {preview?.html && (
            <iframe
              title="Email preview"
              sandbox=""
              srcDoc={preview.html}
              className="h-[45vh] w-full rounded-md border border-border bg-white"
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending || loading || !!error} className="gap-2">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? 'Sending…' : `Send to ${lead.email}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Activity Timeline ───────────────────────────────────────────────────────────

// Emails are stored as the HTML that actually went out; strip it back to text
// so the timeline shows the message rather than markup.
function htmlToText(html) {
  if (!html) return '';
  if (!/<[a-z][\s\S]*>/i.test(html)) return html;   // custom emails are stored as typed
  const el = document.createElement('div');
  el.innerHTML = html;
  el.querySelectorAll('style, script').forEach(n => n.remove());
  return (el.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
}

function TimelineEntry({ item }) {
  const [open, setOpen] = useState(false);

  const failed = item.type === 'email' && item.deliveryStatus === 'failed';
  const visual = {
    email:        { Icon: failed ? MailX : MailCheck, tone: failed ? 'text-destructive' : 'text-primary' },
    stage_change: { Icon: ArrowRight,  tone: 'text-emerald-500' },
    call:         { Icon: PhoneCall,   tone: 'text-sky-500' },
    note:         { Icon: StickyNote,  tone: 'text-muted-foreground' },
    meet_link:    { Icon: Link2,       tone: 'text-violet-500' },
    spam:         { Icon: ShieldAlert, tone: 'text-amber-500' },
  }[item.type] || { Icon: CheckCircle, tone: 'text-muted-foreground' };

  const { Icon, tone } = visual;
  const body = item.type === 'email' ? htmlToText(item.body) : '';

  let headline;
  if (item.type === 'email') {
    headline = item.subject || 'Email';
  } else if (item.type === 'stage_change') {
    headline = item.fromStatus
      ? `${statusMeta(item.fromStatus).label} → ${statusMeta(item.toStatus).label}`
      : statusMeta(item.toStatus).label;
  } else {
    headline = item.note || item.subject || item.type;
  }

  return (
    <div className="flex items-start gap-2.5">
      <Icon className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', tone)} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          <span className="text-xs font-medium text-foreground break-words">{headline}</span>
          {item.type === 'email' && (
            <span className={cn('rounded px-1 py-px text-[10px] font-semibold',
              failed ? 'bg-destructive/10 text-destructive' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400')}>
              {failed ? 'failed' : 'sent'}
            </span>
          )}
          {item.legacy && <span className="rounded bg-muted px-1 py-px text-[10px] text-muted-foreground">history</span>}
        </div>

        {item.type !== 'email' && item.note && item.note !== headline && (
          <p className="mt-0.5 text-xs text-muted-foreground break-words">{item.note}</p>
        )}
        {failed && item.error && (
          <p className="mt-0.5 text-xs text-destructive break-words">{item.error}</p>
        )}

        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
          <span>{fmtDateTime(item.at)}</span>
          {item.by?.name && <span>· {item.by.name}</span>}
          {item.type === 'email' && body && (
            <button type="button" onClick={() => setOpen(o => !o)} className="text-primary hover:underline">
              {open ? 'Hide message' : 'View message'}
            </button>
          )}
        </div>

        {open && body && (
          <pre className="mt-1.5 max-h-56 overflow-auto whitespace-pre-wrap rounded-md bg-muted/50 px-2.5 py-2 text-[11px] leading-relaxed text-foreground/80 font-sans">
            {body}
          </pre>
        )}
      </div>
    </div>
  );
}

function ActivityTimeline({ timeline, loading }) {
  if (loading) {
    return <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading activity…</div>;
  }
  if (!timeline?.length) {
    return <p className="text-xs text-muted-foreground">Nothing logged yet.</p>;
  }
  return (
    <div className="space-y-3">
      {timeline.map((item, i) => <TimelineEntry key={item._id || i} item={item} />)}
    </div>
  );
}

// ── Lead Drawer ─────────────────────────────────────────────────────────────────

function LeadDrawer({ lead: initLead, onClose, onRefresh, onCredentials }) {
  const [lead, setLead]             = useState(initLead);
  const [notes, setNotes]           = useState(initLead.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [modal, setModal]           = useState(null);
  const [timeline, setTimeline]     = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(true);

  useEffect(() => { setLead(initLead); setNotes(initLead.notes || ''); }, [initLead]);

  // The list omits `activity` to stay light, so full detail is loaded here.
  const loadDetail = useCallback(async () => {
    setLoadingTimeline(true);
    try {
      const res = await getLeadById(initLead._id);
      if (res?.lead) setLead(res.lead);
      setTimeline(res?.timeline || []);
    } catch {
      setTimeline([]);
    } finally {
      setLoadingTimeline(false);
    }
  }, [initLead._id]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const meta = statusMeta(lead.status);

  const currentIdx = PIPELINE.findIndex(p => p.key === lead.status);

  const afterChange = (updated) => {
    setLead(updated);
    onRefresh(updated);
    loadDetail();
  };

  const [moveTarget, setMoveTarget] = useState(null);

  const handleStatusAction = (status) => {
    if (status === 'demo_scheduled') { setModal('demo'); return; }
    if (status === 'onboarded')      { setModal('onboard'); return; }
    if (status === 'lost')           { setModal('lost'); return; }
    // Everything else confirms first, so the note and the opt-in email are
    // always on offer rather than only from a separate picker.
    setMoveTarget(status);
    setModal('move');
  };

  // Nothing is sent from here — the confirmation dialog previews it first.
  const [emailStage, setEmailStage] = useState(null);

  const copyMeetLink = async () => {
    try { await navigator.clipboard.writeText(lead.demoMeetLink); toast.success('Meet link copied.'); }
    catch { toast.error('Could not copy.'); }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try { await updateLeadNotes(lead._id, notes); toast.success('Notes saved.'); }
    catch { toast.error('Failed.'); }
    finally { setSavingNotes(false); }
  };

  const actionMeta = {
    contacted:      { label: 'Mark Contacted',  Icon: CheckCircle,  variant: 'outline' },
    demo_scheduled: { label: 'Schedule Demo',    Icon: CalendarPlus, variant: 'outline' },
    demo_completed: { label: 'Mark Demo Done',   Icon: CheckCircle,  variant: 'outline' },
    onboarded:      { label: 'Onboard Customer', Icon: Zap,          variant: 'default' },
    lost:           { label: 'Mark as Lost',     Icon: XCircle,      variant: 'destructive' },
  };

  const handleModalSaved = (statusKey) => {
    setModal(null);
    afterChange({ ...lead, status: statusKey });
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col border-l border-border bg-card shadow-2xl">

        {/* Header */}
        <div className="flex items-start justify-between border-b border-border px-5 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-foreground">{lead.name}</h2>
              <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold',
                lead.intent === 'book_demo'
                  ? 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/20'
                  : 'bg-teal-500/10 text-teal-700 dark:text-teal-400 border-teal-500/20')}>
                {lead.intent === 'book_demo' ? 'Demo' : 'Free Trial'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{lead.company}{lead.companySize ? ` · ${lead.companySize}` : ''}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0 ml-2">
            <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit" onClick={() => setModal('edit')}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Delete" onClick={() => setModal('delete')}><Trash2 className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}><X className="h-4 w-4" /></Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Pipeline progress */}
          <div className="border-b border-border px-5 py-4">
            <p className="mb-2.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Pipeline</p>
            <div className="flex items-center gap-0.5">
              {PIPELINE.filter(p => p.key !== 'lost').map((p, i, arr) => {
                const pIdx    = PIPELINE.findIndex(x => x.key === p.key);
                const curIdx  = PIPELINE.findIndex(x => x.key === lead.status);
                const current = lead.status === p.key;
                const done    = curIdx > pIdx && lead.status !== 'lost';
                return (
                  <div key={p.key} className="flex flex-1 items-center gap-0.5">
                    <div className={cn('flex h-7 flex-1 items-center justify-center rounded text-[10px] font-semibold transition-all',
                      current ? `${p.color} text-white shadow-sm` :
                      done    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' :
                                'bg-muted text-muted-foreground')}>
                      {p.label.split(' ')[0]}
                    </div>
                    {i < arr.length - 1 && <ArrowRight className="h-3 w-3 text-muted-foreground/30 shrink-0" />}
                  </div>
                );
              })}
            </div>
            {lead.status === 'lost' && (
              <div className="mt-2 rounded bg-red-50 px-3 py-1.5 text-xs text-red-700 dark:bg-red-950/30 dark:text-red-300">
                Lost{lead.lostReason ? `: ${lead.lostReason}` : ''}
              </div>
            )}
          </div>

          {/* Contact info */}
          <div className="border-b border-border px-5 py-4 space-y-2">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Contact</p>
            <div className="flex items-center gap-2 text-sm"><Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><a href={`mailto:${lead.email}`} className="text-primary hover:underline">{lead.email}</a></div>
            {lead.phone && <div className="flex items-center gap-2 text-sm"><Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span>{lead.phone}</span></div>}
            <div className="flex items-center gap-2 text-sm"><Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span>{lead.company}{lead.companySize ? ` · ${lead.companySize}` : ''}</span></div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Calendar className="h-3.5 w-3.5 shrink-0" /><span>Submitted {fmtDateTime(lead.createdAt)}</span></div>
          </div>

          {/* Demo & meeting link */}
          <div className="border-b border-border px-5 py-4">
            <div className="mb-2.5 flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Demo</p>
              <Button variant="ghost" size="sm" className="h-6 gap-1 px-2 text-[11px]" onClick={() => setModal('demo')}>
                <Pencil className="h-3 w-3" /> {lead.demoScheduledAt ? 'Edit' : 'Schedule'}
              </Button>
            </div>
            {lead.demoScheduledAt ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-violet-600 dark:text-violet-400">
                  <Clock className="h-3.5 w-3.5 shrink-0" /><span>{fmtDateTime(lead.demoScheduledAt)}</span>
                </div>
                {lead.demoMeetLink ? (
                  <div className="flex items-center gap-1.5">
                    <Video className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <a href={lead.demoMeetLink} target="_blank" rel="noopener noreferrer"
                       className="min-w-0 flex-1 truncate text-xs text-primary hover:underline">{lead.demoMeetLink}</a>
                    <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" title="Copy link" onClick={copyMeetLink}>
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> No meeting link yet — the invite won't contain one.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No demo scheduled.</p>
            )}
          </div>

          {/* Their message */}
          {lead.message && (
            <div className="border-b border-border px-5 py-4">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5"><MessageSquare className="h-3 w-3" /> Their Message</p>
              <p className="whitespace-pre-line rounded-md bg-muted/40 px-3 py-2.5 text-sm leading-relaxed italic text-foreground/80">"{lead.message}"</p>
            </div>
          )}

          {/* Outreach CTA */}
          <div className="border-b border-border px-5 py-4">
            <p className="mb-2.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Outreach</p>
            <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2.5">
              <p className="text-xs text-muted-foreground leading-relaxed">
                {lead.intent === 'book_demo'
                  ? "This lead wants a demo. Send a personalised scheduling email — pre-written based on their current stage."
                  : "This lead wants a free trial. Send an activation email — pre-written based on their current stage."}
              </p>
              <Button size="sm" className="gap-1.5 w-full" onClick={() => setModal('email')}>
                <Send className="h-3.5 w-3.5" />
                {lead.intent === 'book_demo' ? 'Send Demo Scheduling Email' : 'Send Trial Activation Email'}
              </Button>
            </div>

            {/* Stage emails — sendable at any time, independent of the stage */}
            <p className="mt-3.5 mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Send a stage email</p>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(STAGE_EMAILS).map(([stage, label]) => (
                <Button
                  key={stage} variant="outline" size="sm"
                  className="h-7 gap-1.5 px-2.5 text-[11px]"
                  disabled={!!actionLoading}
                  title={`Preview the "${label}" email before sending`}
                  onClick={() => setEmailStage(stage)}
                >
                  <Send className="h-3 w-3" />
                  {label}
                </Button>
              ))}
            </div>
          </div>

          {/* Activity timeline */}
          <div className="border-b border-border px-5 py-4">
            <div className="mb-2.5 flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Activity</p>
              <Button variant="ghost" size="sm" className="h-6 gap-1 px-2 text-[11px]" onClick={() => setModal('log')}>
                <StickyNote className="h-3 w-3" /> Log call / note
              </Button>
            </div>
            <ActivityTimeline timeline={timeline} loading={loadingTimeline} />
          </div>

          {/* Notes */}
          <div className="px-5 py-4">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Internal Notes</p>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Private notes…" rows={4} className="text-sm" />
            <Button size="sm" variant="outline" className="mt-2 gap-1.5" onClick={handleSaveNotes} disabled={savingNotes}>
              {savingNotes ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save Notes
            </Button>
          </div>
        </div>

        {/* Move pipeline footer */}
        <div className="border-t border-border bg-muted/20 px-5 py-4">
          <p className="mb-2.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Move Pipeline</p>
          {/* Every stage is always offered, so nothing disappears as the lead
              progresses and a wrong move is always one click from being undone. */}
          <div className="flex flex-wrap gap-2">
            {PIPELINE.map(p => {
              const am        = actionMeta[p.key] || { label: p.label, Icon: ArrowRight, variant: 'outline' };
              const isCurrent = lead.status === p.key;
              const isBack    = PIPELINE.findIndex(x => x.key === p.key) < currentIdx;
              return (
                <Button
                  key={p.key}
                  variant={isCurrent ? 'secondary' : isBack ? 'ghost' : am.variant}
                  size="sm"
                  disabled={!!actionLoading || isCurrent}
                  title={isCurrent ? 'Current stage' : isBack ? `Move back to ${p.label}` : `Move to ${p.label}`}
                  onClick={() => handleStatusAction(p.key)}
                  className={cn('gap-1.5', isCurrent && 'cursor-default opacity-70',
                    isBack && 'border border-dashed border-border text-muted-foreground')}
                >
                  {actionLoading === p.key
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : isCurrent ? <span className={cn('h-2 w-2 rounded-full', p.color)} />
                    : isBack    ? <ChevronLeft className="h-3.5 w-3.5" />
                                : <am.Icon className="h-3.5 w-3.5" />}
                  {isCurrent ? `${p.label} — current` : am.label}
                </Button>
              );
            })}
          </div>
          <div className="mt-2.5 flex items-center justify-between border-t border-border/60 pt-2.5">
            <p className="text-[11px] text-muted-foreground">Stage changes never email unless you ask.</p>
            <Button
              variant="ghost" size="sm"
              className={cn('h-7 gap-1.5 px-2 text-[11px]', lead.isSpam ? 'text-emerald-600' : 'text-amber-600')}
              onClick={() => setModal('spam')}
            >
              {lead.isSpam ? <><RotateCcw className="h-3 w-3" /> Restore</> : <><Ban className="h-3 w-3" /> Mark spam</>}
            </Button>
          </div>
        </div>
      </aside>

      {modal === 'email'   && <SendEmailModal    lead={lead} onClose={() => setModal(null)} onSent={() => { setModal(null); onRefresh(lead); loadDetail(); }} />}
      {modal === 'edit'    && <EditLeadModal     lead={lead} onClose={() => setModal(null)} onSaved={() => { setModal(null); onRefresh(lead); loadDetail(); }} />}
      {modal === 'move'    && (
        <MoveStageModal
          lead={lead}
          presetTarget={moveTarget}
          onClose={() => { setModal(null); setMoveTarget(null); }}
          onSaved={(s) => { setMoveTarget(null); handleModalSaved(s); }}
        />
      )}
      {modal === 'log'     && <LogActivityModal  lead={lead} onClose={() => setModal(null)} onSaved={() => { setModal(null); loadDetail(); }} />}
      {emailStage && (
        <StageEmailConfirmModal
          lead={lead}
          stage={emailStage}
          onClose={() => setEmailStage(null)}
          onSent={() => { setEmailStage(null); loadDetail(); }}
        />
      )}
      {modal === 'spam'    && <SpamModal         lead={lead} onClose={() => setModal(null)} onSaved={(marked) => { setModal(null); onClose(); onRefresh(null); toast.info(marked ? 'Find it in the Spam tab.' : 'Back in the pipeline.'); }} />}
      {modal === 'delete'  && <DeleteLeadDialog  lead={lead} onClose={() => setModal(null)} onDeleted={() => { setModal(null); onClose(); onRefresh(null); }} />}
      {modal === 'demo'    && <ScheduleDemoModal  lead={lead} onClose={() => setModal(null)} onSaved={() => handleModalSaved('demo_scheduled')} />}
      {modal === 'lost'    && <MarkLostModal      lead={lead} onClose={() => setModal(null)} onSaved={() => handleModalSaved('lost')} />}
      {modal === 'onboard' && (
        <OnboardLeadWizard
          lead={lead}
          onClose={() => setModal(null)}
          onSaved={(result) => { setModal(null); onCredentials?.(result); handleModalSaved('onboarded'); }}
        />
      )}
    </>
  );
}

// ── Lead Row ───────────────────────────────────────────────────────────────────

function LeadRow({ lead, onClick, onEdit, onDelete }) {
  const meta = statusMeta(lead.status);
  return (
    <tr className="border-b border-border transition-colors hover:bg-muted/30 group">
      <td className="py-3 pl-4 pr-2 cursor-pointer" onClick={() => onClick(lead)}>
        <div className="font-medium text-sm text-foreground">{lead.name}</div>
        <div className="text-xs text-muted-foreground">{lead.email}</div>
      </td>
      <td className="px-2 py-3 cursor-pointer" onClick={() => onClick(lead)}>
        <div className="text-sm font-medium">{lead.company}</div>
        <div className="text-xs text-muted-foreground">{lead.companySize || '—'}</div>
      </td>
      <td className="px-2 py-3 cursor-pointer" onClick={() => onClick(lead)}>
        <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold',
          lead.intent === 'book_demo'
            ? 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/20'
            : 'bg-teal-500/10 text-teal-700 dark:text-teal-400 border-teal-500/20')}>
          {lead.intent === 'book_demo' ? 'Demo' : 'Free Trial'}
        </span>
      </td>
      <td className="px-2 py-3 cursor-pointer" onClick={() => onClick(lead)}>
        <div className="flex items-center gap-1.5">
          <span className={cn('h-2 w-2 rounded-full shrink-0', meta.color)} />
          <span className="text-xs font-medium">{meta.label}</span>
        </div>
      </td>
      <td className="px-2 py-3 text-xs text-muted-foreground cursor-pointer" onClick={() => onClick(lead)}>{fmtDate(lead.createdAt)}</td>
      <td className="px-2 py-3 text-xs text-muted-foreground cursor-pointer" onClick={() => onClick(lead)}>
        {lead.demoScheduledAt ? fmtDateTime(lead.demoScheduledAt) : '—'}
      </td>
      <td className="px-2 py-3 pr-3">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit" onClick={e => { e.stopPropagation(); onEdit(lead); }}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" title="Delete" onClick={e => { e.stopPropagation(); onDelete(lead); }}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function SuperAdminLeadsPage() {
  const [leads, setLeads]             = useState([]);
  const [stats, setStats]             = useState({ total: 0, new: 0, contacted: 0, demo_scheduled: 0, demo_completed: 0, onboarded: 0, lost: 0, spam: 0 });
  const [intentStats, setIntentStats] = useState({ book_demo: 0, free_trial: 0 });
  const [loading, setLoading]         = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [total, setTotal]             = useState(0);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editTarget, setEditTarget]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch]           = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterIntent, setFilterIntent] = useState('');
  const [exporting, setExporting]     = useState(false);
  const [tab, setTab]                 = useState('pipeline'); // 'pipeline' | 'spam'
  const [credentials, setCredentials] = useState(null);
  const searchTimer = useRef(null);

  const isSpamTab = tab === 'spam';

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const data = await getLeadStats();
      setStats(data.stats);
      setIntentStats(data.intentStats || { book_demo: 0, free_trial: 0 });
    } catch { /* silent */ } finally { setStatsLoading(false); }
  }, []);

  const fetchLeads = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const data = await getLeads({
        page: p, limit: 20,
        status: filterStatus || undefined,
        intent: filterIntent || undefined,
        search: search || undefined,
        spam: isSpamTab ? 'true' : undefined,
      });
      setLeads(data.leads || []);
      setTotal(data.total || 0);
      setTotalPages(data.pages || 1);
      setPage(p);
    } catch { toast.error('Failed to load leads.'); }
    finally { setLoading(false); }
  }, [filterStatus, filterIntent, search, isSpamTab]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLeads(1); }, [filterStatus, filterIntent, isSpamTab]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchLeads(1), 300);
    return () => clearTimeout(searchTimer.current);
  }, [search]);

  const handleDrawerRefresh = useCallback((updatedLead) => {
    if (updatedLead) {
      setSelectedLead(updatedLead);
      setLeads(prev => prev.map(l => l._id === updatedLead._id ? { ...l, ...updatedLead } : l));
    } else {
      setLeads(prev => prev.filter(l => l._id !== selectedLead?._id));
      setSelectedLead(null);
    }
    fetchStats();
  }, [selectedLead, fetchStats]);

  const handleExport = async () => {
    setExporting(true);
    try { await exportLeads({ status: filterStatus || undefined, intent: filterIntent || undefined }); toast.success('CSV downloaded.'); }
    catch { toast.error('Export failed.'); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Leads"
        description="Inbound leads from your marketing website — track, engage, and convert."
        actions={
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} className="gap-2">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </Button>
        }
      />

      <CredentialsBanner credentials={credentials} onDismiss={() => setCredentials(null)} />

      <StatCards stats={stats} intentStats={intentStats} loading={statsLoading} />

      {/* Pipeline / Spam */}
      <div className="flex items-center gap-1 border-b border-border">
        {[
          { key: 'pipeline', label: 'Pipeline', Icon: Inbox,       count: stats.total },
          { key: 'spam',     label: 'Spam',     Icon: ShieldAlert, count: stats.spam || 0 },
        ].map(({ key, label, Icon, count }) => (
          <button
            key={key}
            onClick={() => { setTab(key); setSelectedLead(null); }}
            className={cn(
              'flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors -mb-px',
              tab === key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
            <span className={cn('rounded-full px-1.5 py-px text-[10px] font-semibold',
              tab === key ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>
              {count}
            </span>
          </button>
        ))}
      </div>

      {isSpamTab && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 text-xs text-amber-700 dark:text-amber-400">
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>
            Leads marked as fake or spam. They're excluded from the pipeline, the stats and the “new leads” badge.
            Restoring one puts it back at the stage it had.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name, email, company…" value={search} onChange={e => setSearch(e.target.value)} className="pl-8 text-sm h-9" />
          {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>}
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring">
          <option value="">All statuses</option>
          {PIPELINE.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <select value={filterIntent} onChange={e => setFilterIntent(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring">
          <option value="">All intents</option>
          <option value="book_demo">Demo Booking</option>
          <option value="free_trial">Free Trial</option>
        </select>
        {(filterStatus || filterIntent || search) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterStatus(''); setFilterIntent(''); setSearch(''); }} className="gap-1.5">
            <X className="h-3.5 w-3.5" /> Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="py-3 pl-4 pr-2 text-left">Contact</th>
                <th className="px-2 py-3 text-left">Company</th>
                <th className="px-2 py-3 text-left">Intent</th>
                <th className="px-2 py-3 text-left">Status</th>
                <th className="px-2 py-3 text-left">Submitted</th>
                <th className="px-2 py-3 text-left">Demo At</th>
                <th className="px-2 py-3 pr-3 text-left w-20"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-16 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={7} className="py-16 text-center">
                  {isSpamTab
                    ? <ShieldAlert className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
                    : <Users className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />}
                  <p className="text-sm text-muted-foreground">
                    {isSpamTab ? 'Nothing marked as spam' : 'No leads found'}
                  </p>
                </td></tr>
              ) : (
                leads.map(lead => (
                  <LeadRow key={lead._id} lead={lead} onClick={setSelectedLead} onEdit={setEditTarget} onDelete={setDeleteTarget} />
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3">
            <p className="text-xs text-muted-foreground">Showing {leads.length} of {total} leads</p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => fetchLeads(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
              <span className="px-2 text-xs text-muted-foreground">{page} / {totalPages}</span>
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => fetchLeads(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
      </div>

      {selectedLead && (
        <LeadDrawer
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onRefresh={handleDrawerRefresh}
          onCredentials={setCredentials}
        />
      )}

      {editTarget && (
        <EditLeadModal lead={editTarget} onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); fetchLeads(page); fetchStats(); if (selectedLead?._id === editTarget._id) setSelectedLead(null); }} />
      )}
      {deleteTarget && (
        <DeleteLeadDialog lead={deleteTarget} onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setLeads(prev => prev.filter(l => l._id !== deleteTarget._id));
            if (selectedLead?._id === deleteTarget._id) setSelectedLead(null);
            setDeleteTarget(null);
            fetchStats();
          }} />
      )}
    </div>
  );
}
